import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ThreeBarsIcon,
  PersonAddIcon,
  QuestionIcon,
  InfoIcon,
  MailIcon,
  CommentIcon,
  ChevronDownIcon,
  SignOutIcon,
  BookIcon,
  XIcon,
  CheckIcon,
  ArrowSwitchIcon,
  CloudIcon,
  DiffIcon,
  RocketIcon,
  EyeIcon,
  LockIcon,
  ShieldIcon,
  SparkleIcon,
  ProjectIcon,
  StarIcon,
  FlowchartIcon,
  CodeIcon,
  HomeIcon,
  DatabaseIcon,
  SearchIcon
} from '@primer/octicons-react';
import { HeaderStyles } from '../../styles/components/Header.css';

// Tool configuration mapping (unchanged)
const TOOL_CONFIG = {
  '/validator': {
    name: 'JSON Validator',
    icon: CheckIcon,
    color: '#10b981',
    description: 'Validate, format, and fix JSON documents',
    path: '/validator'
  },
  '/converter': {
    name: 'JSON Converter',
    icon: ArrowSwitchIcon,
    color: '#8b5cf6',
    description: 'Convert JSON to other formats (YAML, XML, CSV)',
    path: '/converter'
  },
  '/compare': {
    name: 'JSON Compare',
    icon: DiffIcon,
    color: '#f59e0b',
    description: 'Compare two JSON documents and find differences',
    path: '/compare'
  },
  '/viewer': {
    name: 'JSON Viewer',
    icon: EyeIcon,
    color: '#3b82f6',
    description: 'Beautiful tree view for JSON structures',
    path: '/viewer'
  },
  '/masker': {
    name: 'JSON Masker',
    icon: ShieldIcon,
    color: '#059669',
    description: 'Mask sensitive data in JSON documents',
    path: '/masker'
  },
  '/schema': {
    name: 'JSON Schema',
    icon: FlowchartIcon,
    color: '#ec4899',
    description: 'Generate and validate JSON schemas',
    path: '/schema'
  },
  '/visualizer': {
    name: 'Tree Visualizer',
    icon: ProjectIcon,
    color: '#06b6d4',
    description: 'Interactive JSON tree visualization',
    path: '/visualizer'
  },
  '/hosting': {
    name: 'JSON Hosting',
    icon: CloudIcon,
    color: '#6366f1',
    description: 'Host and share JSON files online',
    path: '/hosting'
  },
  '/query': {
    name: 'JSON Query',
    icon: SearchIcon,
    color: '#84cc16',
    description: 'Query JSON data using JSONPath',
    path: '/query'
  },
  '/generator': {
    name: 'Data Generator',
    icon: SparkleIcon,
    color: '#f97316',
    description: 'Generate mock JSON data',
    path: '/generator'
  },
  '/api-tester': {
    name: 'API Tester',
    icon: RocketIcon,
    color: '#ef4444',
    description: 'Test APIs with JSON payloads',
    path: '/api-tester'
  },
  '/fake-api': {
    name: 'Fake REST API',
    icon: DatabaseIcon,
    color: '#14b8a6',
    description: 'Create mock APIs from JSON',
    path: '/fake-api'
  },
  '/inspector': {
    name: 'JSON Inspector',
    icon: SearchIcon,
    color: '#8b5cf6',
    description: 'Inspect JSON structure and metadata',
    path: '/inspector'
  },
  '/schema-validator': {
    name: 'Schema Validator',
    icon: ShieldIcon,
    color: '#0ea5e9',
    description: 'Validate JSON against schemas',
    path: '/schema-validator'
  },
  '/json-score': {
    name: 'JSON Score',
    icon: StarIcon,
    color: '#eab308',
    description: 'Score and analyze JSON quality',
    path: '/json-score'
  },
  '/encoder': {
    name: 'JSON Encoder',
    icon: LockIcon,
    color: '#84cc16',
    description: 'Encode and decode JSON data',
    path: '/encoder'
  },
  '/code-generator': {
    name: 'Code Generator',
    icon: CodeIcon,
    color: '#06b6d4',
    description: 'Generate code from JSON schemas',
    path: '/code-generator'
  },
  '/security-scanner': {
    name: 'Security Scanner',
    icon: ShieldIcon,
    color: '#dc2626',
    description: 'Scan JSON for security vulnerabilities',
    path: '/security-scanner'
  },
  '/learn': {
    name: 'Learn JSON',
    icon: BookIcon,
    color: '#8b5cf6',
    description: 'Learn JSON syntax and best practices',
    path: '/learn'
  },
  '/documentation': {
    name: 'Documentation',
    icon: QuestionIcon,
    color: '#64748b',
    description: 'Tool documentation and guides',
    path: '/documentation'
  },
  '/about': {
    name: 'About',
    icon: InfoIcon,
    color: '#64748b',
    description: 'About JSON Tools platform',
    path: '/about'
  },
  '/contact': {
    name: 'Contact',
    icon: MailIcon,
    color: '#64748b',
    description: 'Contact support team',
    path: '/contact'
  },
  '/feedback': {
    name: 'Feedback',
    icon: CommentIcon,
    color: '#64748b',
    description: 'Send feedback and suggestions',
    path: '/feedback'
  },
};

// Styled components (unchanged)
const HeaderContainer = styled.header`
  ${HeaderStyles.container}
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow: none;
  height: 64px;
  transition: background 0.2s ease;

  @media (max-width: 768px) {
    height: 56px;
    padding: 0 1rem;
  }
`;

const HeaderContent = styled.div`
  ${HeaderStyles.content}
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0;
  }
`;

const Logo = styled.div`
  ${HeaderStyles.logo}
  gap: 0.75rem;
  
  .logo-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      width: 20px;
      height: 20px;
    }
  }
`;

const SidebarToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.border};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const LogoImage = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 6px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }
`;

const ToolName = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const NavRight = styled.div`
  ${HeaderStyles.navRight}
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

// Resource menu styled components
const ResourceMenu = styled.div`
  position: relative;
  display: inline-block;
`;

const ResourceButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.bgSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ResourceDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 160px;
  z-index: 1000;
  overflow: hidden;
  display: ${({ $show }) => ($show ? 'block' : 'none')};
`;

const ResourceItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.bgSecondary};
  }
`;

// Login/User components
const LoginButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: auto;
    
    span {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.375rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
  }
  
  .user-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryDark} 100%);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
  }
  
  .user-email {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.375rem;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
  display: ${({ $show }) => ($show ? 'block' : 'none')};
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.bgSecondary};
  }
  
  &.danger {
    color: ${({ theme }) => theme.colors.error};
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    
    &:hover {
      background: ${({ theme }) => theme.colors.error}10;
    }
  }
`;

// Main Header Component
const Header = ({
  toggleSidebar,
  sidebarState,
  showSidebarToggle = true,
  user,
  onLoginClick,
  onLogout,
  isMobile = false
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showResourceMenu, setShowResourceMenu] = useState(false);
  const resourceMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const INFO_PAGES = ['/learn', '/contact', '/feedback', '/documentation', '/about'];
  const resourceItems = [
    { path: '/learn', icon: <BookIcon size={16} />, label: 'Learn JSON' },
    { path: '/contact', icon: <MailIcon size={16} />, label: 'Contact' },
    { path: '/feedback', icon: <CommentIcon size={16} />, label: 'Feedback' },
    { path: '/documentation', icon: <QuestionIcon size={16} />, label: 'Documentation' },
    { path: '/about', icon: <InfoIcon size={16} />, label: 'About' }
  ];

  // Close ResourceMenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resourceMenuRef.current && !resourceMenuRef.current.contains(event.target)) {
        setShowResourceMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentTool = useMemo(() => {
    const currentPath = location.pathname;
    if (TOOL_CONFIG[currentPath]) return TOOL_CONFIG[currentPath];
    for (const [path, config] of Object.entries(TOOL_CONFIG)) {
      if (currentPath.startsWith(path) && path !== '/') return config;
    }
    return {
      name: 'JSON Tools',
      icon: HomeIcon,
      color: '#3b82f6',
      description: 'A suite of professional JSON utilities',
      path: '/'
    };
  }, [location.pathname]);

  const LOGO_URL = 'https://i.aidevops.tech/b6_logo2.webp';
  const isHomePage = location.pathname === '/' || location.pathname === '';
  const isInfoPage = INFO_PAGES.includes(location.pathname);

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
  };

  const getInitials = (userData) => {
    if (!userData) return '?';
    if (userData.name) {
      const nameParts = userData.name.split(' ');
      if (nameParts.length >= 2) return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      return userData.name.substring(0, 2).toUpperCase();
    }
    if (userData.email) return userData.email.substring(0, 2).toUpperCase();
    return 'U';
  };

  const getToggleTooltip = () => {
    if (sidebarState === 'expanded') return 'Collapse sidebar';
    if (sidebarState === 'collapsed') return 'Expand sidebar';
    return 'Show sidebar';
  };

  const handleToggleClick = () => {
    if (isMobile) {
      toggleSidebar(sidebarState === 'hidden' ? 'expanded' : 'hidden');
    } else {
      if (sidebarState === 'expanded') toggleSidebar('collapsed');
      else if (sidebarState === 'collapsed') toggleSidebar('hidden');
      else toggleSidebar('expanded');
    }
  };

  const handleLogout = () => {
    onLogout();
    setShowUserMenu(false);
  };

  const renderLeftSide = () => {
    if (isHomePage) {
      return (
        <LogoLink to="/" onClick={handleLogoClick} title="JSON Tools Home">
          <LogoImage
            src={LOGO_URL}
            alt="JSON Tools Logo"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%233b82f6"/><text x="50" y="50" font-size="40" text-anchor="middle" dy=".3em" fill="white">J</text></svg>';
            }}
          />
          <ToolName>JSON Tools</ToolName>
        </LogoLink>
      );
    } else if (isInfoPage) {
      return (
        <LogoLink to="/" onClick={handleLogoClick} title="Go to Home">
          <LogoImage
            src={LOGO_URL}
            alt="JSON Tools Logo"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%233b82f6"/><text x="50" y="50" font-size="40" text-anchor="middle" dy=".3em" fill="white">J</text></svg>';
            }}
          />
          <ToolName>{currentTool.name}</ToolName>
        </LogoLink>
      );
    } else {
      return <ToolName>{currentTool.name}</ToolName>;
    }
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo>
          {showSidebarToggle && (
            <SidebarToggle onClick={handleToggleClick} title={getToggleTooltip()}>
              {sidebarState === 'expanded' ? <XIcon size={16} /> : <ThreeBarsIcon size={16} />}
            </SidebarToggle>
          )}
          {renderLeftSide()}
        </Logo>

        <NavRight>
          {/* Desktop-only Resources dropdown */}
          {!isMobile && (
            <ResourceMenu ref={resourceMenuRef}>
              <ResourceButton onClick={() => setShowResourceMenu(!showResourceMenu)}>
                <span>Resources</span>
                <ChevronDownIcon size={16} />
              </ResourceButton>
              <ResourceDropdown $show={showResourceMenu}>
                {resourceItems.map((item) => (
                  <ResourceItem
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowResourceMenu(false)}
                  >
                    {item.icon}
                    {item.label}
                  </ResourceItem>
                ))}
              </ResourceDropdown>
            </ResourceMenu>
          )}
          {/* User / Login section – always visible */}
          {user ? (
            <UserMenu>
              <UserButton onClick={() => setShowUserMenu(!showUserMenu)}>
                <div className="user-avatar">{getInitials(user)}</div>
                {!isMobile && <ChevronDownIcon size={16} />}
              </UserButton>

              <DropdownMenu $show={showUserMenu}>
                <DropdownItem onClick={() => setShowUserMenu(false)}>
                  {user?.name || 'User'}
                </DropdownItem>
                <DropdownItem onClick={handleLogout} className="danger">
                  <SignOutIcon size={16} />
                  Sign Out
                </DropdownItem>
              </DropdownMenu>
            </UserMenu>
          ) : (
            <LoginButton onClick={onLoginClick} title="Sign in to your account">
              <PersonAddIcon size={16} />
              <span>Sign In</span>
            </LoginButton>
          )}
        </NavRight>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;